import i18n from '../utils/locale';
import { getLocalStorageLocaleKey, getLocalStorageTokenKey, isLocalStorageEnabled } from '../utils/localStorage';
import axiosClient from '../utils/axios';
import axios from 'axios';
import { setCompanyStyle } from '../utils/company';
import { notifyError } from '../utils/notify';

export default {

    state: {
        loading: false,

        user: null,
        token: null,

        companySettings: null,
        companySettingsContext: null,

        ceremony: null,
        timelineCeremony: null,

        ceremonyPreview: null,

        locations: null,
        states: null,

        locales: [],
        currentLocale: 'nl',

        filesToStateCheck: [],
        filesToRemoveFromStateCheck: [],

        isUploading: false,
        uploadingIds: [],
    },

    mutations: {
        setLoading(state, loading) {
            state.loading = loading;
        },

        setLocales(state, locales) {
            state.locales = locales;
        },

        setCeremony(state, ceremony) {
            state.ceremony = ceremony;
            state.timelineCeremony = ceremony;
        },

        setCurrentLocale(state, currentLocale) {
            i18n.locale = currentLocale;
            state.currentLocale = currentLocale;
        },

        setUser(state, user) {
            state.user = user;
        },

        setLocations(state, locations) {
            state.locations = locations;
        },

        setStates(state, states) {
            state.states = states;
        },

        setToken(state, token) {
            state.token = token;
            // Set the default authorization headers
            axios.defaults.headers.common['Authorization'] = token ? ('Bearer ' + token) : null;

            if (isLocalStorageEnabled()) {
                if (!token) {
                    localStorage.removeItem(getLocalStorageTokenKey());
                } else {
                    localStorage.setItem(getLocalStorageTokenKey(), token);
                }
            }
        },

        clearFilesToStateCheck(state) {
            state.filesToStateCheck = [];
        },

        addFileForStateCheck(state, file) {
            state.filesToStateCheck.push(file);
        },

        updateFilesToStateCheck(state, files) {
            state.filesToStateCheck = files.filter(file => state.filesToRemoveFromStateCheck.findIndex(fileToFind => fileToFind.id === file.id) === -1);
            state.filesToRemoveFromStateCheck = files.filter(file => file.state !== 3);
        },

        checkFilesToRemoveFromStateCheck(state) {
            state.filesToRemoveFromStateCheck = state.filesToRemoveFromStateCheck.filter(file => state.filesToStateCheck.findIndex(fileToFind => fileToFind.id === file.id) !== -1);
        },

        showCeremonyPreview(state, ceremony) {
            state.ceremonyPreview = ceremony;
        },

        setUploading(state, uploading) {
            state.isUploading = uploading;
        },

        startUploading(state, id) {
            if (state.uploadingIds.indexOf(id) === -1) {
                state.uploadingIds.push(id);
            }

            state.isUploading = !!state.uploadingIds.length;
        },

        stopUploading(state, id) {
            const uploadingIds = state.uploadingIds;
            uploadingIds.splice(uploadingIds.indexOf(id), 1);

            state.isUploading = !!uploadingIds.length;
            state.uploadingIds = uploadingIds;
        },

        setCompanySettings(state, { context, settings }) {
            state.companySettings = settings;
            state.companySettingsContext = context;
        },
    },

    actions: {
        setLocales(context, locales) {
            context.commit('setLocales', locales);
        },

        setLocale(context, locale) {
            if (isLocalStorageEnabled()) {
                localStorage.setItem(getLocalStorageLocaleKey(context.state), locale);
            }

            context.commit('setCurrentLocale', locale);
        },

        login(context, { user, token }) {
            context.commit('setUser', user);
            context.commit('setToken', token);
            context.commit('setLoading', false);
        },

        logout(context) {
            context.commit('setUser', null);
            context.commit('setToken', null);
            context.commit('setLoading', false);
        },

        updateUser(context, { user }) {
            context.commit('setUser', user);
        },

        setLoading(context, loading) {
            context.commit('setLoading', loading);
        },

        setLocations(context, locations) {
            context.commit('setLocations', locations);
        },
        setCeremony(context, ceremony) {
            context.commit('setCeremony', ceremony);
        },

        setStates(context, states) {
            context.commit('setStates', states);
        },

        start(context, current) {
            let savedLocale = isLocalStorageEnabled() ? localStorage.getItem(getLocalStorageLocaleKey()) : current;
            let token = isLocalStorageEnabled() ? localStorage.getItem(getLocalStorageTokenKey()) : null;

            let locale = current ? current : savedLocale;

            if (context.state.locales.indexOf(locale) === -1) {
                locale = 'nl';
            }

            if (locale !== current) {
                context.dispatch('setLocale', locale);
            }

            context.commit('setCurrentLocale', locale);
            context.commit('setToken', token);
        },

        clearFilesToStateCheck(context) {
            context.commit('clearFilesToStateCheck');
        },

        checkFilesToRemoveFromStateCheck(context) {
            context.commit('checkFilesToRemoveFromStateCheck');
        },

        async getCeremony(context, code) {
            context.commit('setLoading', true);

            try {
                const response = await axiosClient(this).get(`/api/ceremonies/code/${code}`);

                context.commit('setCeremony', response.data);
                context.commit('setUploading', false);

            } catch (exception) {
                if (!exception.response || (exception.response && exception.response.status !== 401)) {
                    alert('Cannot load ceremony');
                }
            }

            context.commit('setLoading', false);
        },

        async updateCeremony(context, code) {
            try {
                const response = await axiosClient(this).get(`/api/ceremonies/code/${code}`);

                context.commit('setCeremony', response.data);
            } catch (exception) {
                if (!exception.response || (exception.response && exception.response.status !== 401)) {
                    alert('Cannot load ceremony');
                }
            }
        },

        addFileForStateCheck(context, file) {
            if (context.state.filesToStateCheck.findIndex(fileIndex => fileIndex.id === file.id) !== -1) {
                return;
            }

            context.commit('addFileForStateCheck', file);
        },

        updateFilesToStateCheck(context, files) {
            context.commit('updateFilesToStateCheck', files);
        },

        resetCeremony(context) {
            context.commit('setCeremony', null);
            context.commit('setUploading', false);
        },

        showCeremonyPreview(context, ceremony) {
            context.commit('showCeremonyPreview', ceremony);
        },

        startUploading(context, id) {
            context.commit('startUploading', id);
        },

        stopUploading(context, id) {
            context.commit('stopUploading', id);
        },

        setCompanySettings(context, data) {
            context.commit('setCompanySettings', data);

            setCompanyStyle(data.settings);
        },

        async loadCompanySettings(context, companyContext) {
            try {
                const response = await axiosClient(this).get('/api/company-settings', {
                    params: companyContext,
                });

                await context.dispatch('setCompanySettings', {
                    context: companyContext,
                    settings: response.data,
                });
            } catch (exception) {
                if (exception?.response?.status === 404) {
                    await context.dispatch('setCompanySettings', {
                        context: companyContext,
                        settings: null,
                    });

                    return;
                }

                notifyError(exception);
            }
        },

        async reloadCompanySettings(context) {
            await context.dispatch('loadCompanySettings', context.state.companySettingsContext);
        },
    },

    getters: {
        hasPermission: (state) => (permission) => {
            if (!state.user?.all_permissions?.length) {
                return false;
            }

            return state.user.all_permissions.includes(permission);
        },

        hasAnyPermissions: (state) => (permissions) => {
            if (!permissions.length) {
                return false;
            }

            if (!state.user?.all_permissions?.length) {
                return false;
            }

            return permissions.some(permission => state.user.all_permissions.includes(permission));
        },
    },
};
